import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import useCampaign from './useCampaign';
import {
  Details,
  Design,
  ExclusionRules,
  Recipients,
  Recurrence,
  CampaignRecipientQuery,
  Schedule,
  Attachments,
  Settings
} from './components';
import { actions as templateActions } from '../../store/template';
import { actions as queryTemplateActions } from '../../store/recipientQueryTemplate';
import { actions as attachmentTemplateActions } from '../../store/attachmentTemplate';
import { Button, Snackbar, Tooltip } from '@material-ui/core';
import Alert from 'components/Alert';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import { initiateSaveAndScheduleNow } from 'store/campaign/actions';
import { Alert as AlertScreen } from '@material-ui/lab';
import { CloseIcon } from 'components';
import ScheduleRules from './components/ScheduleRules/ScheduleRules';
import { constants } from '.';
import { scrollController } from 'views/Campaign/helpers/dragAndDropHelper'
import LicenseCheck from 'LicenseCheck';

const {
  getTemplateNamesByUuids,
  updateCommunicationFilters,
  unsetCurrentTemplate
} = templateActions;
const {
  getAttachmentTemplateNamesByUuids,
  updateAttachmentFilters
} = attachmentTemplateActions;
const { getQueryTemplates } = queryTemplateActions;

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: theme.spacing(3)
  },
  alertBox: {
    marginBottom: '5px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    paddingTop: '0px'
  },
  section: {
    marginBottom: theme.spacing()
  },
  cardHeader: {},
  buttonSpacing: {
    marginLeft: '16px'
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  }
}));

const CreateEditCampaign = props => {
  const { className, staticContext, ...rest } = props;
  const isFirstRender = React.useRef(true);

  const dispatch = useDispatch();
  const history = useHistory();
  const { campaignUuid } = useParams();
  const paginatedTemplates = useSelector(store => store.template.paginatedList);
  const templateFilters = useSelector(store => store.template.filters);
  const isTemplateDropdownLoading = useSelector(
    store => store.template.gettingPaginated
  );
  const isTemplateNameHydrationLoading = useSelector(
    store => store.template.gettingNamesByUuids
  );
  const templateSelectedNamesByUuid = useSelector(
    store => store.template.selectedNamesByUuid || {}
  );
  const templateTags = useSelector(store => store.template.tagList || []);
  const paginatedAttachmentTemplates = useSelector(
    store => store.attachmentTemplate.paginatedList
  );
  const attachmentFilters = useSelector(store => store.attachmentTemplate.filters);
  const isAttachmentDropdownLoading = useSelector(
    store => store.attachmentTemplate.gettingPaginated
  );
  const isAttachmentNameHydrationLoading = useSelector(
    store => store.attachmentTemplate.gettingNamesByUuids
  );
  const isCampaignGetting = useSelector(store => store.campaign.getting);
  const attachmentSelectedNamesByUuid = useSelector(
    store => store.attachmentTemplate.selectedNamesByUuid || {}
  );
  const attachmentTags = useSelector(
    store => store.attachmentTemplate.tagList || []
  );
  const queryTemplates = useSelector(store => store.queryTemplate.list);
  const campaign = useCampaign('', '/campaigns/settings');
  const classes = useStyles();

  const {
    thisCampaign,
    closeCampaign,
    showFormSaveError,
    goBack,
    isLoading,
    isCampaignSaved,
    isCampaignSaving,
    isCampaignReadOnly
  } = campaign;
  const isSavedCampaign = !!thisCampaign.uuid;

  const { ON_EVENT } = constants.campaignOccurrenceTypes;
  const licenses = LicenseCheck();
  const { canAccessRecipientQueries } = licenses;

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(unsetCurrentTemplate());
    dispatch(updateCommunicationFilters({ pageNo: 0, searchInput: '' }));
    dispatch(updateAttachmentFilters({ pageNo: 0, searchInput: '' }));
  }, []);

  useEffect(() => {
    if (isFirstRender.current && canAccessRecipientQueries && thisCampaign.campaignOccurrenceType === 'ON_DEMAND') {
      isFirstRender.current = false;
      dispatch(getQueryTemplates()); 
    }
  }, [canAccessRecipientQueries, thisCampaign]);

  useEffect(() => {
    if (
      thisCampaign.hasOwnProperty('hasMovedToNextStep') &&
      !thisCampaign.hasMovedToNextStep &&
      !thisCampaign.name
    ) {
      history.push('/campaigns/settings');
    }
  }, [thisCampaign]);

  useEffect(() => {
    if (isCampaignGetting) {
      return;
    }

    if (campaignUuid) {
      if (
        thisCampaign?.uuid !== campaignUuid ||
        !thisCampaign?._hasSetCampaignExclusionRules
      ) {
        return;
      }
    }

    const communicationTemplateUuids = [
      thisCampaign?.communicationTemplateUuid,
      ...(thisCampaign?.templates || []).map(
        template => template?.communicationTemplateUuid
      )
    ].filter(Boolean);

    if (communicationTemplateUuids.length > 0) {
      dispatch(
        getTemplateNamesByUuids({
          templateUuids: communicationTemplateUuids
        })
      );
    }

    const attachmentTemplateUuids = (
      thisCampaign?.attachmentTemplateUuids || []
    ).filter(Boolean);

    if (attachmentTemplateUuids.length > 0) {
      dispatch(
        getAttachmentTemplateNamesByUuids({
          templateUuids: attachmentTemplateUuids
        })
      );
    }

  }, [
    campaignUuid,
    dispatch,
    isCampaignGetting,
    thisCampaign?.uuid,
    thisCampaign?._hasSetCampaignExclusionRules,
    thisCampaign?.communicationTemplateUuid,
    (thisCampaign?.templates || [])
      .map(template => template?.communicationTemplateUuid)
      .filter(Boolean)
      .join(','),
    (thisCampaign?.attachmentTemplateUuids || []).filter(Boolean).join(',')
  ]);

  const saveAndScheduleNow = () => {
    dispatch(initiateSaveAndScheduleNow());
  };

  return (
    <div className={classes.root}>
      <div className={classes.content}  
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollController(event);
            }}>
        <div className={classes.headerContainer}>
          <div className={classes.alertBox}>
            {isCampaignReadOnly() && (
              <AlertScreen severity="info">
                You can only view this campaign and do not have access to
                edit. Tenant Default campaign can be edited only with 'ALL' property.
              </AlertScreen>
            )}
          </div>
          <CloseIcon
            style={{ paddingTop: '0px', display: 'flex', justifyContent: 'flex-end' }}
            onClickClose={closeCampaign} />
        </div>
        {isSavedCampaign ? (
          <Settings campaign={campaign} className={classes.section} />
        ) : null}
        <Details campaign={campaign} className={classes.section} />
        <Design
          campaign={campaign}
          templates={paginatedTemplates}
          paginatedTemplates={paginatedTemplates}
          templateFilters={templateFilters}
          isTemplateDropdownLoading={isTemplateDropdownLoading}
          templateSelectedNamesByUuid={templateSelectedNamesByUuid}
          isTemplateNameHydrationLoading={isTemplateNameHydrationLoading}
          templateTags={templateTags}
          className={classes.section}
        />
        <ExclusionRules campaign={campaign} className={classes.section} />
        {thisCampaign.campaignOccurrenceType === ON_EVENT &&
          <ScheduleRules campaign={campaign} className={classes.section} />}
        <Recipients campaign={campaign} className={classes.section} />
        <CampaignRecipientQuery
          campaign={campaign}
          queryTemplates={queryTemplates}
          className={classes.section}
        />
        <Recurrence campaign={campaign} className={classes.section} />
        <Schedule campaign={campaign} className={classes.section} />

        <Attachments
          campaign={campaign}
          templates={paginatedAttachmentTemplates}
          paginatedTemplates={paginatedAttachmentTemplates}
          attachmentFilters={attachmentFilters}
          isAttachmentDropdownLoading={isAttachmentDropdownLoading}
          isAttachmentNameHydrationLoading={isAttachmentNameHydrationLoading}
          attachmentSelectedNamesByUuid={attachmentSelectedNamesByUuid}
          attachmentTags={attachmentTags}
          className={classes.section}
        />
      </div>

      <div className={classes.footer}>
        <div>
          {!isSavedCampaign && (
            <Button color="secondary" variant="contained" onClick={goBack}>
              Back
            </Button>
          )}
        </div>
        <div>
          {campaign.thisCampaign.scheduleRule?.scheduleNow && (
            <Tooltip title="Please schedule date&time">
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={true}
                  onClick={campaign.saveCampaign}>
                  Save Campaign
                </Button>
              </span>
            </Tooltip>
          )}
          {!campaign.thisCampaign.scheduleRule?.scheduleNow && !isCampaignReadOnly() && (
            <Button
              color="primary"
              variant="contained"
              disabled={isLoading || isCampaignSaving}
              onClick={campaign.saveCampaign}>
              Save Campaign
            </Button>
          )}
          {campaign.thisCampaign.campaignOccurrenceType === 'ON_DEMAND' &&
            !campaign.thisCampaign.recurrence && (
              <Tooltip title="Schedules the campaign immediately and resets if any Schedule Date&Time is set">
                <span>
                  {!isCampaignReadOnly() && (
                  <Button
                    className={classes.buttonSpacing}
                    color="primary"
                    variant="contained"
                    disabled={isLoading || isCampaignSaving}
                    onClick={saveAndScheduleNow}>
                    Save Campaign & Schedule now
                  </Button>
                  )}
                </span>
              </Tooltip>
            )}
        </div>
        <Snackbar open={showFormSaveError()} autoHideDuration={6000}>
          <Alert show={showFormSaveError()} severity="error">
            Please correct the issues with the campaign above
          </Alert>
        </Snackbar>
      </div>
      <SaveMessage openSnackbar={isCampaignSaved} />
    </div>
  );
};

export default CreateEditCampaign;
